import React, { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { aulasService, turmasService } from '../services/database'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  PlayIcon,
  EyeIcon,
  ClockIcon,
  CalendarIcon
} from '@heroicons/react/24/outline'
import type { Database } from '../lib/supabaseClient'

type Aula = Database['public']['Tables']['aulas']['Row']
type AulaInsert = Database['public']['Tables']['aulas']['Insert']

const AulasPage = () => {
  const { user, profile } = useAuth()
  const [aulas, setAulas] = useState<Aula[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingAula, setEditingAula] = useState<Aula | null>(null)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    video_url: '',
    duration: ''
  })

  useEffect(() => {
    fetchAulas()
  }, [])

  const fetchAulas = async () => {
    try {
      setLoading(true)
      let data: Aula[] = []

      if (profile?.role === 'aluno') {
        // Import dynamic or use standard
        // We need to add turmasService to import
        data = await turmasService.getMyAulas(user?.id!)
      } else {
        data = await aulasService.getAll()
      }

      setAulas(data)
    } catch (error) {
      console.error('Erro ao carregar aulas:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredAulas = aulas.filter(aula =>
    aula.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    aula.description?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (editingAula) {
        // Atualizar aula existente
        const updatedAula = await aulasService.update(editingAula.id, {
          title: formData.title,
          description: formData.description || null,
          video_url: formData.video_url || null,
          duration: formData.duration || null
        })

        if (updatedAula) {
          setAulas(prev => prev.map(aula =>
            aula.id === editingAula.id ? updatedAula : aula
          ))
        }
      } else {
        // Criar nova aula
        const newAulaData: AulaInsert = {
          title: formData.title,
          description: formData.description || null,
          video_url: formData.video_url || null,
          duration: formData.duration || null,
          created_by: user?.id || null
        }

        const newAula = await aulasService.create(newAulaData)
        if (newAula) {
          setAulas(prev => [newAula, ...prev])
        }
      }

      resetForm()
      setShowModal(false)
    } catch (error) {
      console.error('Erro ao salvar aula:', error)
    }
  }

  const handleEdit = (aula: Aula) => {
    setEditingAula(aula)
    setFormData({
      title: aula.title,
      description: aula.description || '',
      video_url: aula.video_url || '',
      duration: aula.duration || ''
    })
    setShowModal(true)
  }

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir esta aula?')) {
      try {
        await aulasService.delete(id)
        setAulas(prev => prev.filter(aula => aula.id !== id))
      } catch (error) {
        console.error('Erro ao excluir aula:', error)
      }
    }
  }

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      video_url: '',
      duration: ''
    })
    setEditingAula(null)
  }

  const getEmbedUrl = (videoUrl: string) => {
    if (videoUrl.includes('youtube.com/watch?v=')) {
      const videoId = videoUrl.split('v=')[1]?.split('&')[0]
      return `https://www.youtube.com/embed/${videoId}`
    }
    if (videoUrl.includes('youtu.be/')) {
      const videoId = videoUrl.split('youtu.be/')[1]?.split('?')[0]
      return `https://www.youtube.com/embed/${videoId}`
    }
    return videoUrl
  }

  const canCreateAula = profile?.role === 'admin' || profile?.role === 'professor'
  const canEditAula = (aula: Aula) => {
    return profile?.role === 'admin' ||
      (profile?.role === 'professor' && aula.created_by === user?.id)
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        <p className="text-gray-500 animate-pulse font-medium">Carregando aulas...</p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg p-8 shadow-bizu-lg text-white flex flex-col md:flex-row justify-between items-center gap-6">
        <div>
          <h1 className="text-3xl font-bold">Conteúdo Acadêmico</h1>
          <p className="text-primary-100 mt-2 text-lg">Explore as videoaulas e materiais de estudo disponíveis.</p>
        </div>
        {canCreateAula && (
          <button
            onClick={() => {
              resetForm()
              setShowModal(true)
            }}
            className="w-full md:w-auto bg-white text-primary-600 px-6 py-3 rounded-xl hover:bg-primary-50 flex items-center justify-center gap-2 font-bold shadow-sm transition-all active:scale-95"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Nova Aula</span>
          </button>
        )}
      </div>

      {/* Search & Stats */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-grow group">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
          <input
            type="text"
            placeholder="O que você deseja aprender hoje?"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 pr-4 py-3.5 w-full bg-white border border-gray-100 rounded-2xl shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none text-gray-700"
          />
        </div>
        <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-2xl border border-gray-100 shadow-sm whitespace-nowrap">
          <span className="text-sm font-bold text-gray-900">{filteredAulas.length}</span>
          <span className="text-sm text-gray-500">Aulas encontradas</span>
        </div>
      </div>

      {/* Aulas Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredAulas.map((aula) => (
          <div key={aula.id} className="group bg-white rounded-2xl shadow-sm hover:shadow-bizu-lg overflow-hidden border border-gray-100 transition-all duration-300 flex flex-col">
            {/* Video Thumbnail */}
            <div className="aspect-video bg-gray-900 relative overflow-hidden">
              {aula.video_url ? (
                <>
                  <iframe
                    src={getEmbedUrl(aula.video_url)}
                    title={aula.title}
                    className="w-full h-full opacity-80 group-hover:opacity-100 transition-opacity"
                    allowFullScreen
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <PlayIcon className="h-12 w-12 text-gray-300" />
                </div>
              )}
              
              {aula.duration && (
                <div className="absolute bottom-3 right-3 bg-black/70 backdrop-blur-md text-white text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider">
                  {aula.duration}
                </div>
              )}
            </div>

            {/* Content */}
            <div className="p-6 flex flex-col flex-grow">
              <h3 className="font-bold text-lg text-gray-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-1">{aula.title}</h3>
              {aula.description && (
                <p className="text-gray-500 text-sm mb-6 line-clamp-2 leading-relaxed flex-grow">{aula.description}</p>
              )}

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-50 mt-auto">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1 text-[11px] font-bold text-gray-400 uppercase">
                    <CalendarIcon className="w-3.5 h-3.5" />
                    <span>{new Date(aula.created_at).toLocaleDateString('pt-BR')}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {canEditAula(aula) && (
                    <>
                      <button
                        onClick={() => handleEdit(aula)}
                        className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all"
                        title="Editar aula"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(aula.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        title="Excluir aula"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </>
                  )}
                  {aula.video_url && (
                    <a
                      href={aula.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 bg-primary-50 text-primary-600 hover:bg-primary-600 hover:text-white rounded-lg transition-all shadow-sm"
                      title="Assistir aula"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredAulas.length === 0 && (
        <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
          <div className="bg-gray-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <PlayIcon className="w-10 h-10 text-gray-200" />
          </div>
          <div className="text-gray-900 font-bold text-xl mb-2">Nenhuma aula encontrada</div>
          <p className="text-gray-500 mb-8 max-w-xs mx-auto">Não encontramos nenhuma aula com os termos buscados. Tente novamente.</p>
          {canCreateAula && (
            <button
              onClick={() => {
                resetForm()
                setShowModal(true)
              }}
              className="bg-primary-600 text-white px-6 py-2.5 rounded-xl hover:bg-primary-700 font-bold transition-all shadow-md active:scale-95"
            >
              Criar primeira aula
            </button>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-lg shadow-2xl animate-in fade-in zoom-in duration-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <div className="p-2 bg-primary-50 rounded-lg">
                {editingAula ? <PencilIcon className="w-6 h-6 text-primary-600" /> : <PlusIcon className="w-6 h-6 text-primary-600" />}
              </div>
              {editingAula ? 'Editar Aula' : 'Nova Aula'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 ml-1">
                  Título do Conteúdo
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ex: Introdução ao Direito Constitucional"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 ml-1">
                  Descrição Curta
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  placeholder="Descreva o que será abordado nesta aula..."
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none resize-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 ml-1">
                    URL do Vídeo
                  </label>
                  <input
                    type="url"
                    value={formData.video_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, video_url: e.target.value }))}
                    placeholder="YouTube ou Vimeo"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 ml-1">
                    Duração
                  </label>
                  <div className="relative">
                    <ClockIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={formData.duration}
                      onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                      placeholder="Ex: 45:00"
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false)
                    resetForm()
                  }}
                  className="flex-1 px-6 py-3 text-gray-500 font-bold hover:bg-gray-50 rounded-xl transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-xl font-bold hover:bg-primary-700 shadow-md shadow-primary-200 active:scale-95 transition-all"
                >
                  {editingAula ? 'Salvar Alterações' : 'Publicar Aula'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default AulasPage